import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, User } from '@auth0/auth0-angular';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DataService } from '../../data.service';
import { SnackbarComponent } from '../snackbar/snackbar.component';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { checkIfAuth0User } from 'src/app/utils/checkIfAuth0User';
import { SNACKBAR_CLOSE } from '../../utils/snackbar';

@Component({
    selector: 'app-change-email-flow-content',
    templateUrl: './change-email-flow-content.component.html',
    styleUrls: ['./change-email-flow-content.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChangeEmailFlowContentComponent implements OnInit, OnChanges {
  @Input() user: User; // The type should match the expected data type
  step = 1;
  changeEmailForm: FormGroup;
  isLoading = false;
  isAuth0User = true;

  constructor(
    public auth: AuthService,
    public translate: TranslateService,
    private formBuilder: FormBuilder,
    private _snackBar: MatSnackBar,
    private dataService: DataService,
    private router: Router
  ) {}
  ngOnInit() {
    this.changeEmailForm = this.formBuilder.group({
      currentEmail: [{ value: '', disabled: true }, Validators.required],
      // c3a4m checks it before it sends a code: a token alone cannot change the email (C3-06).
      currentPassword: ['', Validators.required],
      code: ['', Validators.required],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.user && changes.user.currentValue) {
      this.isAuth0User = checkIfAuth0User(this.user.sub);
      const emailControl = this.changeEmailForm.get('currentEmail');
      if (!this.isAuth0User) {
        emailControl?.disable();
      } else {
        emailControl?.enable();
      }
    }
  }

  verificationCodeStep() {
    if (!this.isAuth0User) {
      return this.translate
        .get('errors.isAuth0User')
        .subscribe((translatedError: string) => {
          this._snackBar.open(translatedError, SNACKBAR_CLOSE, {
            duration: 3000,
            verticalPosition: 'top',
            politeness: 'assertive',
            panelClass: ['error-snackbar'],
          });
        });
    }
    const email = this.changeEmailForm.get('currentEmail').value;
    if (!this.validateEmail(email)) {
      this.translate
        .get('errors.invalidEmail')
        .subscribe((translatedError: string) => {
          this._snackBar.open(translatedError, SNACKBAR_CLOSE, {
            duration: 3000,
            verticalPosition: 'top',
            politeness: 'assertive',
            panelClass: ['error-snackbar'],
          });
        });

      return;
    }
    if (email === this.user.email) {
      this._snackBar.openFromComponent(SnackbarComponent, {
        duration: 3000,
        verticalPosition: 'top',
        politeness: 'assertive',
        data: { messageTranslationId: 'errors.identicalEmail' },
        panelClass: ['error-snackbar'],
      });
      return;
    }
    if (!this.changeEmailForm.get('currentPassword').value) {
      this.showSnack('errors.currentPassword', 'error-snackbar');
      return;
    }
    this.sendVerificationCode(email, false);
  }

  sendVerificationCode(email: string, isResend: boolean) {
    this.isLoading = true;
    const currentPassword = this.changeEmailForm.get('currentPassword').value;
    this.dataService
      .postData(environment.changeEmailEndpoint, { email, currentPassword })
      .subscribe({
        next: (response) => {
          if (!isResend) {
            this.step++;
          } else {
            this._snackBar.openFromComponent(SnackbarComponent, {
              duration: 3000,
              verticalPosition: 'top',
              politeness: 'assertive',
              data: {
                messageTranslationId: 'changeEmailFlow.codeResentFeedback',
              },
              panelClass: ['success-snackbar'],
            });
          }
          this.isLoading = false;
        },
        error: (error) => {
          // Only c3a4m's password check answers with a currentPassword error; a 401 for an
          // expired access token is a general failure.
          if (error?.status === 401 && error?.error?.errors?.[0]?.currentPassword) {
            this.showSnack('errors.currentPassword', 'error-snackbar');
          } else {
            this.showSnack('errors.failedEmailChange', 'success-snackbar');
          }
          this.isLoading = false;
        },
      });
  }

  confirmChangeEmail() {
    this.isLoading = true;

    const code = this.changeEmailForm.get('code').value;
    const email = this.changeEmailForm.get('currentEmail').value;

    if (this.changeEmailForm.invalid || !code) {
      this.translate
        .get('errors.invalidCode')
        .subscribe((translatedError: string) => {
          this._snackBar.open(translatedError, SNACKBAR_CLOSE, {
            duration: 3000,
            verticalPosition: 'top',
            politeness: 'assertive',
            panelClass: ['error-snackbar'],
          });
        });
      return;
    }

    const data = {
      code,
      email,
    };

    this.dataService
      .postData(environment.changeEmailConfirmEndpoint, data)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.step++;
        },
        error: (error) => {
          const message = error?.error?.message;
          if (message === 'Invalid code') {
            this.showSnack('errors.invalidCode', 'error-snackbar');
          } else if (message === 'Code expired') {
            this.showSnack('errors.codeExpired', 'error-snackbar');
          } else if (message === 'Email already in use') {
            // The pending change is gone: start again with another address.
            this.showSnack('errors.emailInUse', 'error-snackbar');
            this.step = 1;
          } else {
            this.showSnack('errors.failedEmailChange', 'error-snackbar');
          }
          this.isLoading = false;
        },
      });
  }

  private showSnack(messageTranslationId: string, panelClass: string) {
    this._snackBar.openFromComponent(SnackbarComponent, {
      duration: 3000,
      verticalPosition: 'top',
      politeness: 'assertive',
      data: { messageTranslationId },
      panelClass: [panelClass],
    });
  }

  validateEmail(email) {
    const emailRegex =
      /^(?!.*\.{2})[a-zA-Z0-9._%+-]+(?:\.[a-zA-Z0-9._%+-]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  resendCode() {
    const email = this.changeEmailForm.get('currentEmail').value;
    this.sendVerificationCode(email, true);
  }

  goToLogin() {
    this.auth.logout();
  }

  handleBack() {
    // Handle back button click
    this.router.navigate(['/']);
  }
}
